import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import { Credential } from '../types';
import { ArrowLeft, Eye, EyeOff, Copy, Edit, Trash2, Clock, Share2, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import CredentialModal from '../components/CredentialModal';
import ShareModal from '../components/ShareModal';
import ShareManagement from '../components/ShareManagement';
import './CredentialDetail.css';

const CredentialDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showShareManagement, setShowShareManagement] = useState(false);
  const [revealedPassword, setRevealedPassword] = useState(false);

  const { data, isLoading } = useQuery(
    ['credential', id],
    async () => {
      const response = await api.get(`/credentials/${id}`);
      return response.data;
    },
    { enabled: !!id }
  );

  const { data: historyData } = useQuery(
    ['credential-history', id],
    async () => {
      const response = await api.get(`/audit/credentials/${id}`);
      return response.data;
    },
    { enabled: !!id }
  );

  const deleteMutation = useMutation(
    async () => {
      await api.delete(`/credentials/${id}`);
    },
    {
      onSuccess: () => {
        toast.success('Credential deleted successfully');
        navigate('/credentials');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to delete credential');
      },
    }
  );

  const credential: Credential = data;

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
      setTimeout(async () => {
        await navigator.clipboard.writeText('');
      }, 30000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this credential?')) {
      deleteMutation.mutate();
    }
  };

  if (isLoading) {
    return <div className="loading">Loading credential...</div>;
  }

  if (!credential) {
    return (
      <div className="error-state">
        <p>Credential not found</p>
        <Link to="/credentials" className="btn-primary">
          <ArrowLeft size={20} />
          <span>Back to Credentials</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="credential-detail">
      <div className="detail-header">
        <Link to="/credentials" className="back-link">
          <ArrowLeft size={20} />
          <span>Back</span>
        </Link>
        <div className="header-actions">
          {credential.permissions.canView && (
            <>
              <button className="btn-secondary" onClick={() => setShowShareManagement(true)}>
                <Users size={20} />
                <span>Manage Shares</span>
              </button>
              <button className="btn-primary" onClick={() => setShowShareModal(true)}>
                <Share2 size={20} />
                <span>Share</span>
              </button>
            </>
          )}
          {credential.permissions.canEdit && (
            <button className="btn-secondary" onClick={() => setShowEditModal(true)}>
              <Edit size={20} />
              <span>Edit</span>
            </button>
          )}
          {credential.permissions.canDelete && (
            <button className="btn-danger" onClick={handleDelete}>
              <Trash2 size={20} />
              <span>Delete</span>
            </button>
          )}
        </div>
      </div>

      <div className="detail-content">
        <div className="detail-main">
          <div className="detail-card">
            <div className="card-header">
              <h1>{credential.name}</h1>
              <div className="badges">
                <span className="badge">{credential.category.replace(/_/g, ' ')}</span>
                <span className="badge">{credential.type.replace(/_/g, ' ')}</span>
              </div>
            </div>

            {/* Show warning if credential is from deleted user */}
            {credential.tags && credential.tags.some((tag: string) => tag === 'deleted-user') && (
              <div className="detail-section warning-section">
                <div className="deleted-user-badge" style={{ marginBottom: '1rem', padding: '0.75rem 1rem', fontSize: '0.875rem' }}>
                  ⚠️ This credential was transferred from a deleted user. Check notes for original creator information.
                </div>
              </div>
            )}

            {credential.description && (
              <div className="detail-section">
                <h3>Description</h3>
                <p>{credential.description}</p>
              </div>
            )}

            {credential.data && credential.permissions.canView && (
              <div className="detail-section">
                <h3>Credentials</h3>
                <div className="credentials-grid">
                  {credential.data.username && (
                    <div className="credential-field">
                      <label>Username</label>
                      <div className="field-value">
                        <span>{credential.data.username}</span>
                        <button
                          className="copy-btn"
                          onClick={() => handleCopy(credential.data!.username!, 'Username')}
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                  {credential.data.password && (
                    <div className="credential-field">
                      <label>Password</label>
                      <div className="field-value">
                        <span className="password-field">
                          {revealedPassword ? credential.data.password : '••••••••'}
                        </span>
                        <div className="field-actions">
                          <button
                            className="copy-btn"
                            onClick={() => handleCopy(credential.data!.password!, 'Password')}
                          >
                            <Copy size={16} />
                          </button>
                          <button
                            className="copy-btn"
                            onClick={() => setRevealedPassword(!revealedPassword)}
                          >
                            {revealedPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {credential.data.host && (
                    <div className="credential-field">
                      <label>Host</label>
                      <div className="field-value">
                        <span>{credential.data.host}</span>
                        <button
                          className="copy-btn"
                          onClick={() => handleCopy(credential.data!.host!, 'Host')}
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                  {credential.data.port && (
                    <div className="credential-field">
                      <label>Port</label>
                      <div className="field-value">
                        <span>{credential.data.port}</span>
                        <button
                          className="copy-btn"
                          onClick={() => handleCopy(String(credential.data!.port), 'Port')}
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                  {credential.data.token && (
                    <div className="credential-field">
                      <label>API Token</label>
                      <div className="field-value">
                        <span className="password-field">
                          {revealedPassword ? credential.data.token : '••••••••'}
                        </span>
                        <div className="field-actions">
                          <button
                            className="copy-btn"
                            onClick={() => handleCopy(credential.data!.token!, 'Token')}
                          >
                            <Copy size={16} />
                          </button>
                          <button
                            className="copy-btn"
                            onClick={() => setRevealedPassword(!revealedPassword)}
                          >
                            {revealedPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {credential.tags && credential.tags.length > 0 && (
              <div className="detail-section">
                <h3>Tags</h3>
                <div className="tags">
                  {credential.tags.map((tag, idx) => {
                    const isDeletedUserTag = tag === 'deleted-user' || tag.startsWith('transferred-from-');
                    return (
                      <span 
                        key={idx} 
                        className={`tag ${isDeletedUserTag ? (tag === 'deleted-user' ? 'deleted-user' : 'transferred-from') : ''}`}
                      >
                        {tag === 'deleted-user' ? '⚠️ Deleted User' : tag.startsWith('transferred-from-') ? `🔄 ${tag.replace('transferred-from-', 'From: ')}` : tag}
                    </span>
                    );
                  })}
                </div>
              </div>
            )}

            {credential.notes && (
              <div className="detail-section">
                <h3>Notes</h3>
                <p className="notes">{credential.notes}</p>
              </div>
            )}

            <div className="detail-meta">
              <div className="meta-item">
                <label>Created By</label>
                <span>{credential.createdBy.username}</span>
              </div>
              <div className="meta-item">
                <label>Created At</label>
                <span>{new Date(credential.createdAt).toLocaleString()}</span>
              </div>
              <div className="meta-item">
                <label>Last Updated</label>
                <span>{new Date(credential.updatedAt).toLocaleString()}</span>
              </div>
              {credential.expiresAt && (
                <div className="meta-item">
                  <label>Expires At</label>
                  <span className={new Date(credential.expiresAt) < new Date() ? 'expired' : ''}>
                    {new Date(credential.expiresAt).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="detail-sidebar">
          <div className="detail-card">
            <h3>History</h3>
            <div className="history-list">
              {historyData?.history?.length === 0 ? (
                <div className="empty-state">No history available</div>
              ) : (
                historyData?.history?.map((item: any) => (
                  <div key={item.id} className="history-item">
                    <div className="history-action">{item.action}</div>
                    <div className="history-time">
                      <Clock size={14} />
                      {new Date(item.createdAt).toLocaleString()}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {showShareManagement && (
        <div className="modal-overlay" onClick={() => setShowShareManagement(false)}>
          <div className="modal-content share-management-modal" onClick={(e) => e.stopPropagation()}>
            <ShareManagement
              credentialId={credential.id}
              onClose={() => setShowShareManagement(false)}
            />
          </div>
        </div>
      )}

      {showEditModal && (
        <CredentialModal
          credential={credential}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            queryClient.invalidateQueries(['credential', id]);
          }}
        />
      )}

      {showShareModal && credential && (
        <ShareModal
          credentialId={credential.id}
          credentialName={credential.name}
          onClose={() => setShowShareModal(false)}
          onSuccess={() => {
            setShowShareModal(false);
            queryClient.invalidateQueries(['credential', id]);
          }}
        />
      )}
    </div>
  );
};

export default CredentialDetail;

